import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import times from 'lodash/times';
import moment from 'moment';
import axios from 'axios';
import uuidV4 from 'uuid/v4';
import APIUtils, {APIRegistry} from '@spothero/utils/api';
import Config from 'config';
import SearchUtils from 'search/search-utils';
import SearchTileUtils from 'search/search-tile-utils';
import {getFOVFromLatLngBounds} from 'utils/google-maps';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import {retrieveTravelDistances} from 'utils/travel-distance';

export const SPOTS_GET_RADIAL = 'SPOTS_GET_RADIAL';
export const SPOTS_GET_TILED = 'SPOTS_GET_TILED';
export const SPOTS_GET_AIRPORT = 'SPOTS_GET_AIRPORT';
export const SPOTS_GET_MOBILE = 'SPOTS_GET_MOBILE';
export const SPOTS_GET_MOBILE_PAGINATION = 'SPOTS_GET_MOBILE_PAGINATION';
export const SPOTS_GET_TRAVEL_DISTANCES = 'SPOTS_GET_TRAVEL_DISTANCES';
export const SPOTS_RADIAL_INCREMENT = 'SPOTS_RADIAL_INCREMENT';
export const SPOTS_TILED_INCREMENT = 'SPOTS_TILED_INCREMENT';
export const SPOTS_CANCEL_REQUESTS = 'SPOTS_CANCEL_REQUESTS';
export const SPOTS_SET_SPOT_SELECTED_RATE = 'SPOTS_SET_SPOT_SELECTED_RATE';

const cancelSources = [];
const cancelCurrentRequests = message => {
    while (cancelSources.length) {
        cancelSources[0].cancel(message);
        cancelSources.shift();
    }
};
const checkForErrorsInResponse = args => {
    const errorData = [];

    forEach(args, responseData => {
        // error object is different for <500 and >=500 status codes
        const status =
            get(responseData, 'status') || get(responseData, 'response.status');

        if (status && status >= 400) {
            const errors =
                get(responseData, 'data.data.errors') ||
                get(responseData, 'response.data.data.errors');

            if (errors) {
                errorData.push(...errors);
            }
        }
    });

    return errorData;
};
let radialMapCenterLatitude;
let radialMapCenterLongitude;

const assignParams = ({
    searchStateParams,
    mapBounds,
    activeAmenities,
    searchText,
    isDefaultTimes,
    limit,
}) => {
    const segmentId = SegmentUtils.getAnonymousId();
    const {
        sessionUUID,
        searchUUID,
        actionUUID,
        action,
    } = SearchTracking.getValues();
    /* eslint-disable camelcase */
    const newParams = {
        session_uuid: sessionUUID,
        search_id: searchUUID,
        action_id: actionUUID,
        action,
        is_default: isDefaultTimes,
        search_text: searchText,
        ...(mapBounds && getFOVFromLatLngBounds(mapBounds)),
        limit,
        segment_id: segmentId,
        mixpanel_id: segmentId,
    };
    /* eslint-enable camelcase */

    if (searchStateParams.monthly) {
        // eslint-disable-next-line camelcase
        searchStateParams.monthly_start_date = moment(
            searchStateParams.starts
        ).format(Config.urlDateFormat);

        delete searchStateParams.starts;
        delete searchStateParams.ends;
    } else {
        delete searchStateParams.monthly_start_date;
    }

    if (activeAmenities) {
        activeAmenities.forEach(activeAmenity => {
            const prefix = activeAmenity.substring(0, 3);

            if (prefix === 'no-') {
                if (activeAmenity === 'no-contract') {
                    newParams.amenity_no_contract = true; // eslint-disable-line camelcase
                } else {
                    newParams[
                        `amenity_${activeAmenity
                            .replace('no-', '')
                            .replace('-', '_')}`
                    ] = false;
                }
            } else {
                newParams[`amenity_${activeAmenity.replace('-', '_')}`] = true;
            }
        });
    }

    return {
        ...searchStateParams,
        ...newParams,
    };
};

export const cancelSpotRequests = () => {
    cancelCurrentRequests();

    return {
        type: SPOTS_CANCEL_REQUESTS,
    };
};

export const getTravelDistances = dispatch => {
    return dispatch({
        type: SPOTS_GET_TRAVEL_DISTANCES,
        payload: APIUtils.all(retrieveTravelDistances()),
    });
};

export const getSpotsRadial = ({dateTimeChanged = false, autoZoom = false}) => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const mapBounds = prevSearchState.searchMapInstance.getBounds();
        const searchState = SearchUtils.prepareRequest({
            requestType: 'radial',
            bounds: mapBounds,
            searchState: prevSearchState,
            searchRequestState,
        });
        const {
            params: {eid: eventId, monthly: isMonthly},
            requestIncrements,
            radialDistances,
            geolocated,
            isDefaultTimes,
        } = searchState;
        const url = eventId
            ? `events/${eventId}/rates/`
            : isMonthly
            ? 'facilities/rates/monthly/'
            : 'facilities/rates/';
        const requests = [];
        const destinationTitle = destination ? destination.title : '';
        let trackingAction = eventId ? 'EVENT' : 'SEARCH';

        if (geolocated) {
            trackingAction = 'FIND_ME';
        }

        if (dateTimeChanged) {
            trackingAction = 'DATE_CHANGED';
        }

        if (autoZoom) {
            trackingAction = 'AUTO_ZOOM';
        }

        SearchTracking.updateAction(trackingAction);

        const params = assignParams({
            searchStateParams: searchState.params,
            mapBounds,
            isDefaultTimes,
            activeAmenities: prevState.filters.activeAmenities,
            searchText: searchState.params.search_string || destinationTitle,
        });

        // radial requests re-calculate the center based on map center so we want to make sure we re-use the
        // same exact center for future tiled map requests below in pan/zoom/etc.
        radialMapCenterLatitude = params.latitude;
        radialMapCenterLongitude = params.longitude;

        times(requestIncrements, () => {
            const data = {
                ...params,
                distance__gt: Number(radialDistances[0]), // eslint-disable-line camelcase
                distance__lt: Number(radialDistances[1]), // eslint-disable-line camelcase
            };
            const cancelSource = axios.CancelToken.source();
            const config = {
                cancelToken: cancelSource.token,
            };

            radialDistances.shift();
            cancelSources.push(cancelSource);

            requests.push(
                searchAPIUtils
                    .get(url, data, config)
                    .then(response => {
                        const responseData = response.data.data.results;

                        dispatch({
                            type: SPOTS_RADIAL_INCREMENT,
                            payload: {
                                results: responseData,
                                searchRequestState,
                                timezone: prevState.city.data.timezone,
                            },
                        });

                        return responseData;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        });

        return dispatch({
            type: SPOTS_GET_RADIAL,
            payload: searchAPIUtils
                .all(requests)
                .then(
                    searchAPIUtils.spread((...args) => {
                        const errors = checkForErrorsInResponse(args);

                        if (errors.length) {
                            return Promise.reject(errors);
                        } else {
                            getTravelDistances(dispatch).catch(error => {
                                // silently catch this error to ignore errors being logged
                            });
                        }
                    })
                )
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements,
            },
        });
    };
};

export const getSpotsTiled = bounds => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const hasDestination = !isEmpty(destination);
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const mapBounds =
            bounds || prevSearchState.searchMapInstance.getBounds();
        const searchState = SearchUtils.prepareRequest({
            requestType: 'tiled',
            bounds: mapBounds,
            searchState: prevSearchState,
            searchRequestState,
        });
        const {
            params: {eid: eventId, monthly: isMonthly},
            searchMapInstance,
            tiles,
            requestIncrements,
            isDefaultTimes,
        } = searchState;
        const requests = [];
        const url = eventId
            ? `events/${eventId}/rates/`
            : isMonthly
            ? 'facilities/rates/monthly/'
            : 'facilities/rates/';
        const searchText = searchState.params.search_string
            ? searchState.params.search_string
            : hasDestination
            ? destination.title
            : null;
        const params = {
            ...assignParams({
                searchStateParams: searchState.params,
                activeAmenities: prevState.filters.activeAmenities,
                mapBounds,
                isDefaultTimes,
                searchText,
            }),
            // re-use center lat/long from radial request since that is the true map center to calculate distances off of
            latitude: radialMapCenterLatitude,
            longitude: radialMapCenterLongitude,
        };

        while (tiles.length) {
            const data = {
                ...params,
                ...SearchTileUtils.getTileData(tiles[0], searchMapInstance),
            };
            const cancelSource = axios.CancelToken.source();
            const config = {
                cancelToken: cancelSource.token,
            };

            tiles.shift();
            cancelSources.push(cancelSource);

            requests.push(
                searchAPIUtils
                    .get(url, data, config)
                    .then(response => {
                        const responseData = response.data.data.results;

                        dispatch({
                            type: SPOTS_TILED_INCREMENT,
                            payload: {
                                results: responseData,
                                searchRequestState,
                                timezone: prevState.city.data.timezone,
                            },
                        });

                        return responseData;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        }

        return dispatch({
            type: SPOTS_GET_TILED,
            payload: searchAPIUtils
                .all(requests)
                .then(
                    searchAPIUtils.spread((...args) => {
                        const errors = checkForErrorsInResponse(args);

                        if (errors.length) {
                            return Promise.reject(errors);
                        }
                        // temp disable travel distance request on all but initial spots
                        // } else {
                        //     getTravelDistances(dispatch)
                        //         .catch(error => {
                        //             // silently catch this error to ignore errors being logged
                        //         });
                        // }
                    })
                )
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements,
            },
        });
    };
};

export const getSpotsMobile = ({
    dateTimeChanged = false,
    limit = 15,
    minDistance = 0,
    maxDistance = 1609,
}) => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const hasDestination = !isEmpty(destination);
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const searchState = SearchUtils.prepareRequest({
            requestType: 'mobile',
            bounds: null,
            searchState: prevSearchState,
            searchRequestState,
        });
        const {
            params: {eid: eventId, monthly: isMonthly},
            isDefaultTimes,
        } = searchState;
        const url = eventId
            ? `events/${eventId}/rates/`
            : isMonthly
            ? 'facilities/rates/monthly/'
            : 'facilities/rates/';
        const cancelSource = axios.CancelToken.source();
        const config = {
            cancelToken: cancelSource.token,
        };

        let trackingAction = eventId ? 'LIST_EVENT' : 'LIST_SEARCH';

        if (dateTimeChanged) {
            trackingAction = 'LIST_DATE_CHANGED';
        }

        if (minDistance) {
            trackingAction = 'LIST_SEARCH_NEXT';
        }

        SearchTracking.updateAction(trackingAction);

        const searchText = searchState.params.search_string
            ? searchState.params.search_string
            : hasDestination
            ? destination.title
            : null;
        const params = assignParams({
            searchStateParams: searchState.params,
            activeAmenities: prevState.filters.activeAmenities,
            isDefaultTimes,
            searchText,
            limit,
        });

        const data = {
            ...params,
            /* eslint-disable camelcase */
            distance__gt: minDistance,
            distance__lt:
                minDistance > maxDistance
                    ? minDistance + 1609
                    : !maxDistance || maxDistance < 1609 // ensure max distance is at least 1 mile
                    ? 1609
                    : maxDistance,
            /* eslint-enable camelcase */
        };

        cancelSources.push(cancelSource);

        return dispatch({
            type: minDistance ? SPOTS_GET_MOBILE_PAGINATION : SPOTS_GET_MOBILE,
            payload: searchAPIUtils
                .get(url, data, config)
                .then(response => {
                    // temp disable travel distance request on all but initial spots
                    if (!minDistance) {
                        getTravelDistances(dispatch).catch(error => {
                            // silently catch this error to ignore errors being logged
                        });
                    }

                    return {
                        results: response.data.data.results,
                        searchRequestState,
                        timezone: prevState.city.data.timezone,
                    };
                })
                .catch(error => {
                    return error;
                }),
            meta: {
                isMobileRequest: true,
            },
        });
    };
};

export const getSpotsAirport = () => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const requestId = uuidV4();
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        const prevState = getState();
        const destination = prevState.destination.data;
        const hasDestination = !isEmpty(destination);
        const prevSearchState = prevState.search.data;
        const searchRequestState = prevState.searchRequest;
        const searchState = SearchUtils.prepareRequest({
            requestType: 'airport',
            searchState: prevSearchState,
            searchRequestState,
        });
        const {isDefaultTimes, params: searchStateParams} = searchState;
        const cancelSource = axios.CancelToken.source();
        const config = {
            cancelToken: cancelSource.token,
        };

        SearchTracking.updateAction('LIST_SEARCH');

        const searchText = searchStateParams.search_string
            ? searchStateParams.search_string
            : hasDestination
            ? destination.title
            : null;
        const params = assignParams({
            searchStateParams,
            activeAmenities: prevState.filters.activeAmenities,
            isDefaultTimes,
            searchText,
        });

        // iata is required for airport facility rates search
        params.iata = destination.airport.iata_code;

        cancelSources.push(cancelSource);

        return dispatch({
            type: SPOTS_GET_AIRPORT,
            payload: searchAPIUtils
                .get('facilities/rates/airport/', params, config)
                .then(response => {
                    getTravelDistances(dispatch).catch(error => {
                        // silently catch this error to ignore errors being logged
                    });

                    return {
                        results: response.data.data.results,
                        searchRequestState,
                        timezone: prevState.city.data.timezone,
                        requestId,
                    };
                })
                .catch(error => {
                    error.requestId = requestId;

                    return Promise.reject(error);
                }),
            meta: {
                requestId,
            },
        });
    };
};

export const setSpotSelectedRate = spotWithNewRate => {
    return {
        type: SPOTS_SET_SPOT_SELECTED_RATE,
        payload: {
            spotWithNewRate,
        },
    };
};
